import {
  Box,
  Grid,
  IconButton,
  Paper,
  Typography,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import React, { useEffect, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import WSButton from "../../Components/Button/WSButton";
import WSTextField, { TextType } from "../../Components/TextBox/WSTextBox";
import WSSelectField from "../../Components/Select/WSSelect";
import WSTextAreaField from "../../Components/WSTextArea/WSTextArea";
import WSDateField from "../../Components/DateField/WSDateField";
import { callAPI, Method, fetchAllPaginated, formatDate } from "../../commonFunctions/CommonFunctions";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";

interface Item {
  ItemId: string;
  ItemName: string;
}

interface Warehouse {
  WarehouseId: string;
  WarehouseName: string;
}

interface Supplier {
  SupplierId: string;
  SupplierName: string;
}

const statusOptions = [
  { value: "Draft", label: "Draft" },
  { value: "Ordered", label: "Ordered" },
  { value: "Received", label: "Received" },
  { value: "Closed", label: "Closed" },
  { value: "Cancelled", label: "Cancelled" },
];

export const PurchaseOrder: React.FC<any> = React.memo(() => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);
  const { showMessage } = useSnackbar();

  const [loading, setLoading] = useState(false);

  const [items, setItems] = useState<Item[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm<any>({
    mode: "onChange",
    defaultValues: {
      SupplierId: "",
      WarehouseId: "",
      PONumber: "",
      BillNumber: "",
      BillDate: null,
      Remarks: "",
      Status: "Ordered",
      items: [{ ItemId: "", Quantity: "", Price: "" }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    name: "items",
    control,
  });

  const fetchDropdownData = async () => {
    try {
      setLoading(true);
      const [allItems, allWarehouses, allSuppliers] = await Promise.all([
        fetchAllPaginated("items/list"),
        fetchAllPaginated("warehouses/list"),
        fetchAllPaginated("suppliers/list"),
      ]);

      setItems(allItems);
      setWarehouses(allWarehouses);
      setSuppliers(allSuppliers);
    } catch (error) {
      console.error("Failed to fetch dropdown data:", error);
      showMessage("Failed to load dropdown data", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchPurchaseOrderData = async (poId: string) => {
    try {
      setLoading(true);
      const response = await callAPI(`purchaseorder/${poId}`, Method.GET);

      if (response?.success !== false && response?.data) {
        const po = response.data;
        console.log("Purchase order data received from GET purchaseorder/:id ->", po);
        reset({
          SupplierId: po.SupplierId || "",
          WarehouseId: po.WarehouseId || "",
          PONumber: po.PONumber || "",
          BillNumber: po.BillNumber || "",
          BillDate: po.BillDate || null,
          Remarks: po.Remarks || "",
          Status: po.Status || "Ordered",
          items: po.items?.length
            ? po.items.map((item: any) => ({
                ItemId: item.ItemId || "",
                Quantity: item.Quantity?.toString() || "",
                Price: item.Price?.toString() || "",
              }))
            : [{ ItemId: "", Quantity: "", Price: "" }],
        });
      } else {
        showMessage(response?.message || `Purchase order ${poId} not found`, "error");
      }
    } catch (error: any) {
      console.error("Failed to load purchase order:", error);
      showMessage("Failed to load purchase order data", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDropdownData();
    if (isEditMode && id) {
      fetchPurchaseOrderData(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onSubmit = async (data: any) => {
    try {
      setLoading(true);

      const payload = {
        SupplierId: data.SupplierId,
        WarehouseId: data.WarehouseId,
        PONumber: data.PONumber?.trim() || "",
        BillNumber: data.BillNumber?.trim() || "",
        BillDate: data.BillDate ? formatDate(data.BillDate) : "",
        Remarks: data.Remarks?.trim() || "",
        Status: data.Status || "Ordered",
        items: (data.items || []).map((item: any) => ({
          ItemId: item.ItemId,
          Quantity: parseInt(item.Quantity) || 0,
          Price: parseFloat(item.Price) || 0,
        })),
      };

      console.log(
        isEditMode ? `Purchase order payload sent to PUT purchaseorder/update/${id} ->` : "Purchase order payload sent to POST purchaseorder/create ->",
        payload
      );

      let response;
      if (isEditMode && id) {
        response = await callAPI(`purchaseorder/update/${id}`, Method.PUT, payload);
      } else {
        response = await callAPI("purchaseorder/create", Method.POST, payload);
      }

      if (response?.success !== false) {
        showMessage(
          isEditMode ? "Purchase order updated successfully!" : "Purchase order created successfully!",
          "success"
        );
        navigate("/purchase-order/list");
      } else {
        showMessage(response?.message || "Failed to save purchase order", "error");
      }
    } catch (error: any) {
      const backendData = error?.response?.data;
      console.error("Failed to save purchase order. Backend response ->", backendData);

      let backendMessage = "Failed to save purchase order";
      if (typeof backendData === "string") {
        backendMessage = backendData;
      } else if (typeof backendData?.detail === "string") {
        backendMessage = backendData.detail;
      } else if (Array.isArray(backendData?.detail)) {
        backendMessage = backendData.detail[0]?.msg || backendMessage;
      } else if (backendData?.message) {
        backendMessage = backendData.message;
      }

      if (backendMessage.length > 100) {
        backendMessage = `${backendMessage.slice(0, 100)}...`;
      }

      showMessage(backendMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2, background: "#f0f0f0", minHeight: "100vh" }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography sx={{ fontSize: "20px", fontWeight: 600 }}>
          {isEditMode ? "Edit Purchase Order" : "Purchase Order"}
        </Typography>
        <Tooltip title="Back to List" arrow>
          <IconButton
            onClick={() => navigate("/purchase-order/list")}
            sx={{ background: "black", color: "#fff", "&:hover": { background: "black" } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Paper
        sx={{
          borderRadius: "16px",
          p: 3,
          background: "#fafafa",
          position: "relative",
        }}
      >
        {loading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(255,255,255,0.9)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
              borderRadius: "16px",
            }}
          >
            <CircularProgress />
          </Box>
        )}

        <Box display="flex" justifyContent="space-between" mb={7}>
          <Typography sx={{ fontSize: "18px", fontWeight: 500 }}>
            Purchase Order Details
          </Typography>
          <Tooltip title={isEditMode ? "Update Purchase Order" : "Create Purchase Order"} arrow>
            <IconButton
              sx={{ background: "#000", color: "#fff" }}
              onClick={handleSubmit(onSubmit)}
              disabled={!isValid || loading}
            >
              <SaveIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Grid container rowSpacing={8} columnSpacing={5}>
          <Grid item xs={12} md={4}>
            <WSSelectField
              name="SupplierId"
              label="Supplier"
              id="SupplierId"
              control={control}
              rules={{ required: "Supplier is required" }}
              inlineSearch
              options={suppliers.map((supplier) => ({
                value: supplier.SupplierId,
                label: supplier.SupplierName,
              }))}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <WSSelectField
              name="WarehouseId"
              label="Warehouse"
              id="WarehouseId"
              control={control}
              rules={{ required: "Warehouse is required" }}
              inlineSearch
              options={warehouses.map((warehouse) => ({
                value: warehouse.WarehouseId,
                label: warehouse.WarehouseName,
              }))}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <WSSelectField
              name="Status"
              label="Status"
              id="Status"
              control={control}
              options={statusOptions}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <WSTextField
              name="PONumber"
              label="PO Number"
              id="PONumber"
              control={control}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <WSTextField
              name="BillNumber"
              label="Bill Number"
              id="BillNumber"
              control={control}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <WSDateField
              name="BillDate"
              label="Bill Date"
              id="BillDate"
              dateFormat="DD-MM-YYYY"
              control={control}
              rules={{ required: false }}
            />
          </Grid>

          <Grid item xs={12}>
            <WSTextAreaField
              name="Remarks"
              label="Remarks"
              id="Remarks"
              control={control}
            />
          </Grid>
        </Grid>

        <Typography sx={{ fontSize: "18px", fontWeight: 500, mt: 8, mb: 3 }}>
          Items
        </Typography>

        <Grid container rowSpacing={4} columnSpacing={5}>
          {fields.map((row, index) => (
            <React.Fragment key={row.id}>
              <Grid item xs={12} md={5}>
                <WSSelectField
                  name={`items.${index}.ItemId`}
                  label="Item"
                  id={`item-${index}`}
                  control={control}
                  rules={{ required: "Item is required" }}
                  inlineSearch
                  matchAnyWord
                  options={items.map((item) => ({
                    value: item.ItemId,
                    label: item.ItemName,
                  }))}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <WSTextField
                  label="Quantity"
                  id={`quantity-${index}`}
                  name={`items.${index}.Quantity`}
                  control={control}
                  rules={{ required: true, min: 1 }}
                  type={TextType.AllowOnlyNumber}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <WSTextField
                  label="Price"
                  id={`price-${index}`}
                  name={`items.${index}.Price`}
                  control={control}
                  rules={{ required: true, min: 0 }}
                  type={TextType.Number}
                />
              </Grid>

              <Grid item xs={12} md={1} display="flex" alignItems="center" justifyContent="center">
                <IconButton
                  onClick={() => fields.length > 1 && remove(index)}
                  disabled={fields.length === 1}
                  size="small"
                >
                  <DeleteIcon fontSize="small" color="error" />
                </IconButton>
              </Grid>
            </React.Fragment>
          ))}
        </Grid>

        <Box sx={{ mt: 3 }}>
          <WSButton
            id="addItemButton"
            type="button"
            label="+ Add Item"
            size="small"
            onClick={() => append({ ItemId: "", Quantity: "", Price: "" })}
          />
        </Box>
      </Paper>
    </Box>
  );
});

export default PurchaseOrder;
